import React from "react";
import { CheckCircle2 } from "lucide-react";
import DraggablePanel from "./DraggablePanel";
import { useSelectionStore } from "../../stores/selectionStore";
import { useSceneStore } from "../../stores/sceneStore";
import {
  useModelValidationActions,
  useModelValidationFindings,
  useModelValidationIsStale,
  useModelValidationStatus,
  useModelValidationSummary,
} from "../../stores/modelValidationStore";

const severityMeta = {
  error: {
    label: "Errors",
    dotClassName: "bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.45)]",
    pillClassName: "bg-red-950/60 text-red-200 border-red-800",
  },
  warning: {
    label: "Warnings",
    dotClassName: "bg-amber-300 shadow-[0_0_8px_rgba(252,211,77,0.45)]",
    pillClassName: "bg-amber-950/60 text-amber-200 border-amber-800",
  },
  info: {
    label: "Info",
    dotClassName: "bg-sky-300 shadow-[0_0_8px_rgba(125,211,252,0.45)]",
    pillClassName: "bg-sky-950/60 text-sky-200 border-sky-800",
  },
};

const formatTime = (value) => (value ? new Date(value).toLocaleTimeString() : "Not checked yet");

const SummaryCount = React.memo(function SummaryCount({ severity, count }) {
  const meta = severityMeta[severity];
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-300">
      <span className={`h-2.5 w-2.5 rounded-full ${meta.dotClassName}`} aria-hidden />
      <span className="font-semibold text-gray-100">{count}</span>
      <span>{meta.label}</span>
    </div>
  );
});

const findGeomBodyUuid = (bodies, geomUuid) => {
  for (const body of Object.values(bodies)) {
    if ((body.geoms || []).some((geom) => String(geom.uuid) === String(geomUuid))) {
      return body.uuid;
    }
  }
  return null;
};

const selectTarget = (finding, bodies) => {
  const selection = useSelectionStore.getState();
  const target =
    finding.targets.find((candidate) => candidate.kind !== "contact") || finding.targets[0];
  if (!target) return;

  selection.setLastSelectionSource("modelCheck");

  switch (target.kind) {
    case "body":
      selection.selectBody(target.uuid);
      break;
    case "joint":
      selection.selectJoint(target.uuid);
      break;
    case "actuator":
      selection.selectActuator(target.uuid);
      break;
    case "site":
      selection.selectSite(target.uuid);
      break;
    case "geom": {
      const bodyUuid = findGeomBodyUuid(bodies, target.uuid);
      selection.selectGeom(bodyUuid ? `${bodyUuid}:${target.uuid}` : target.uuid);
      break;
    }
    case "mesh":
      selection.selectMesh(target.uuid);
      break;
    case "equality":
      selection.selectEquality(target.uuid);
      break;
    case "tendon":
      selection.selectTendon(target.uuid);
      break;
    case "camera":
      selection.selectCamera(target.uuid);
      break;
    default:
      break;
  }
};

const FindingRow = React.memo(function FindingRow({ finding, bodies }) {
  const meta = severityMeta[finding.severity];
  const primaryTarget = finding.targets[0];

  return (
    <button
      type="button"
      className="w-full rounded border border-gray-700/80 bg-gray-900/70 p-2.5 text-left hover:border-gray-600 hover:bg-gray-800/80"
      onClick={() => selectTarget(finding, bodies)}
    >
      <div className="flex items-start gap-2.5">
        <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${meta.dotClassName}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-100">{finding.title}</span>
            <span className={`rounded border px-1.5 py-0.5 text-[10px] ${meta.pillClassName}`}>
              {finding.ruleId}
            </span>
          </div>
          <div className="mt-1 text-xs leading-5 text-gray-200">{finding.message}</div>
          {primaryTarget ? (
            <div className="mt-1.5 text-xs text-gray-400">
              {primaryTarget.kind}: {primaryTarget.label || primaryTarget.uuid}
            </div>
          ) : null}
        </div>
      </div>
    </button>
  );
});

const FindingSection = React.memo(function FindingSection({ severity, findings, bodies }) {
  if (!findings.length) return null;
  const meta = severityMeta[severity];

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-300">
        <span className={`h-2.5 w-2.5 rounded-full ${meta.dotClassName}`} aria-hidden />
        <span>{meta.label}</span>
        <span className="rounded bg-gray-800 px-1.5 py-0.5 text-[10px] text-gray-300">
          {findings.length}
        </span>
      </div>
      {findings.map((finding) => (
        <FindingRow key={finding.id} finding={finding} bodies={bodies} />
      ))}
    </section>
  );
});

const ModelCheckPanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const status = useModelValidationStatus();
  const isStale = useModelValidationIsStale();
  const findings = useModelValidationFindings();
  const { summary, checkedAt, error } = useModelValidationSummary();
  const { run } = useModelValidationActions();
  const bodies = useSceneStore((state) => state.bodies);

  const findingsBySeverity = React.useMemo(
    () => ({
      error: findings.filter((finding) => finding.severity === "error"),
      warning: findings.filter((finding) => finding.severity === "warning"),
      info: findings.filter((finding) => finding.severity === "info"),
    }),
    [findings]
  );

  const isChecking = status === "checking";
  const hasRun = status === "ready" || status === "error";
  const hasFindings = summary.findingCount > 0;

  React.useEffect(() => {
    if (!open || isChecking) return;
    if (!hasRun) {
      run();
      return;
    }
    if (!isStale) return;

    const timer = window.setTimeout(() => {
      run();
    }, 150);

    return () => window.clearTimeout(timer);
  }, [hasRun, isChecking, isStale, open, run]);

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Check Model"
      titleIcon={titleIcon}
      initialSize={{ width: 520, height: 560 }}
      minWidth={380}
      minHeight={320}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden text-gray-200">
        <div className="space-y-3 border-b border-gray-800 p-3">
          <div>
            <div className="text-sm font-semibold text-gray-100">Static pre-flight checks</div>
            <div className="mt-1 text-xs leading-5 text-gray-400">
              Checks scene references, inertials, mass contributors, joint limits, and actuator
              coverage before playback.
            </div>
          </div>

          {isStale ? (
            <div className="rounded border border-amber-700/70 bg-amber-950/30 px-3 py-2 text-xs text-amber-100">
              Scene changed since the last check. Refreshing findings...
            </div>
          ) : null}
          {status === "error" && error ? (
            <div className="rounded border border-red-800/70 bg-red-950/30 px-3 py-2 text-xs text-red-100">
              {error}
            </div>
          ) : null}

          <div className="flex items-center gap-4 whitespace-nowrap">
            <SummaryCount severity="error" count={summary.errorCount} />
            <SummaryCount severity="warning" count={summary.warningCount} />
            <SummaryCount severity="info" count={summary.infoCount} />
          </div>
          <div className="text-xs text-gray-400">Checked {formatTime(checkedAt)}</div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-3">
          {!hasRun ? (
            <div className="flex h-full items-center justify-center rounded border border-dashed border-gray-700 p-6 text-center text-xs text-gray-300">
              Checking the current scene before physics starts.
            </div>
          ) : !hasFindings ? (
            <div className="flex h-full flex-col items-center justify-center rounded border border-green-800/70 bg-green-950/20 p-6 text-center text-sm text-green-100">
              <CheckCircle2 className="mb-2 h-4 w-4" />
              No static model issues found.
            </div>
          ) : (
            <div className="space-y-5">
              <FindingSection
                severity="error"
                findings={findingsBySeverity.error}
                bodies={bodies}
              />
              <FindingSection
                severity="warning"
                findings={findingsBySeverity.warning}
                bodies={bodies}
              />
              <FindingSection severity="info" findings={findingsBySeverity.info} bodies={bodies} />
            </div>
          )}
        </div>
      </div>
    </DraggablePanel>
  );
};

export default React.memo(ModelCheckPanel);
